import { DEFAULT_PREVIEW, MEDIA_TYPES } from 'src/static/constants';

// 기본 미디어 파일 타입
export type MediaFile = File | string;

// drag&drop 이벤트: 신규 등록 파일(File), DB저장된 파일(string)을 drag drop이벤트에 사용하기 위한 타입 정의
export interface FileItem {
  value: MediaFile;
  id: string;
}

type FileUploadResult = {
  paths: string[]; // 기존 서버에 있는 경로 (URL)
  uploadedFiles: File[]; // 새로 업로드된 파일
};

type FileTypeInfo = {
  type: string;
  preview: string;
};

const REACT_APP_API_URL = process.env.REACT_APP_API_URL || '';

const DEFAULT_FILE_INFO: FileTypeInfo = {
  type: 'unsupported',
  preview: DEFAULT_PREVIEW,
};

/**
 * !URL 생성 (파일 미리보기)
 *
 * @param file - File 객체 또는 문자열 URL
 * @returns 완성된 URL
 */
export const getFileUrl = (file: MediaFile): string => {
  if (!file) return '';

  // 1. File 객체인 경우
  if (file instanceof File) {
    return URL.createObjectURL(file);
  }

  // 2. 이미 전체 URL인 경우
  if (file.startsWith('http') || file.startsWith('blob')) {
    return file;
  }

  // 3. 문자열(경로)인 경우
  return `${REACT_APP_API_URL}${file}`;
};

/**
 * !타입 반환 (파일 미리보기)
 * @param file - File 객체 또는 문자열 경로
 * @returns MIME 타입
 */
export const getMediaType = (file: MediaFile): string => {
  if (!file) return 'application/octet-stream';

  // 1. File 객체인 경우
  if (file instanceof File) {
    return file.type;
  }
  // 2. 문자열 경로인 경우 확장자로 판단
  if (typeof file === 'string') {
    const extension = file.split('.').pop()?.toLowerCase() || '';
    return MEDIA_TYPES[extension as keyof typeof MEDIA_TYPES] || 'application/octet-stream';
  }

  return 'application/octet-stream';
};

/**
 * !toFileItem 함수 수정 (drag&drop를 위해 file타입, string타입 모두 타입맞추고, 고유 ID 생성)
 * @param file - 파일 객체 또는 문자열 경로
 * @param index - 인덱스
 * @returns FileItem
 */
export const toFileItem = (file: MediaFile, index: number): FileItem => ({
  id: file instanceof File ? `file-${file.name}-${file.lastModified}-${index}` : `file-${index}`,
  value: file,
});

/**
 * !등록 & 수정 시 사용: 업로드한 파일과 api에서 받아온 path 분리
 *
 * @param files - 최종적으로 업로드되어있는 파일 리스트
 * @returns - 업로드된 파일과 api에서 받아온 path 분리
 */
export const separateFilesAndUrls = (files: Array<File | string>): FileUploadResult => {
  const isString = (file: unknown): file is string => typeof file === 'string';

  // 문자열 필터링
  const paths = files.filter(isString).map((path) => {
    if (typeof path === 'string' && path.startsWith(REACT_APP_API_URL)) {
      return path.replace(REACT_APP_API_URL, '');
    }
    return path;
  });

  // 업로드된 파일 추출
  const uploadedFiles = files.filter((file) => file instanceof File) as File[];

  return { paths, uploadedFiles };
};

/**
 * !파일 타입과 미리보기 정보 반환 (RHFUploadDynamic)
 * @param file - File 객체 또는 문자열 경로
 * @param accept - 허용 가능한 파일 형식
 */
export const getFileType = (
  file: MediaFile,
  accept: Record<string, any>,
): { type: string; preview: string } => {
  const defaultPreview = '/assets/icons/files/ic_document.svg';

  // 1. 확장자 추출
  const getExtension = (filename: string): string => filename.split('.').pop()?.toLowerCase() || '';

  // 2. 파일 타입 정보 반환
  const getFileTypeInfo = (extension: string): FileTypeInfo => {
    const typeKey = EXTENSION_MAP[extension];
    return typeKey ? FILE_TYPE_MAP[typeKey] : DEFAULT_FILE_INFO;
  };

  // 3-1. 검증하는 file 확장자가 accept 확장자에 포함하는지 boolean으로 반환
  const isAcceptedExtension = (extension: string, accept: Record<string, any>): boolean => {
    for (const type in accept) {
      const extensions = type.split('/')[1]?.split(',');
      if (extensions?.includes(extension)) {
        return true;
      }
    }
    return false;
  };

  // 3-2. accept 파일 타입 확인 (ex. jpg, jpeg, png, gif는 image)
  const getFileTypeFromAccept = (fileType: string, accept: Record<string, any>): string | null => {
    for (const type in accept) {
      if (fileType === type || fileType.startsWith(type.split('/')[0])) {
        return type.split('/')[0];
      }
    }
    return null;
  };

  // 1. 문자열 URL 처리
  if (typeof file === 'string') {
    const extension = getExtension(file);
    if (!extension) return DEFAULT_FILE_INFO;

    if (isAcceptedExtension(extension, accept)) {
      return EXTENSION_MAP[extension]
        ? getFileTypeInfo(extension)
        : { type: 'unknown', preview: DEFAULT_FILE_INFO.preview };
    }

    return DEFAULT_FILE_INFO;
  }

  // 2. File 객체 처리
  if (file instanceof File) {
    const extension = getExtension(file.name);
    const fileType = getFileTypeFromAccept(file.type, accept);
    if (fileType) {
      return fileType === 'application'
        ? getFileTypeInfo(extension)
        : { type: fileType, preview: DEFAULT_FILE_INFO.preview };
    }

    return DEFAULT_FILE_INFO;
  }

  return DEFAULT_FILE_INFO;
};

// 파일 타입별 정보 매핑
const FILE_TYPE_MAP: Record<string, FileTypeInfo> = {
  pdf: {
    type: 'pdf',
    preview: '/assets/icons/files/ic_pdf.svg',
  },
  excel: {
    type: 'excel',
    preview: '/assets/icons/files/ic_excel.svg',
  },
  powerpoint: {
    type: 'ppt',
    preview: '/assets/icons/files/ic_power_point.svg',
  },
  zip: {
    type: 'zip',
    preview: '/assets/icons/files/ic_zip.svg',
  },
  word: {
    type: 'word',
    preview: '/assets/icons/files/ic_word.svg',
  },
  image: {
    type: 'image',
    preview: '/assets/icons/files/ic_img.svg',
  },
  video: {
    type: 'video',
    preview: '/assets/icons/files/ic_video.svg',
  },
  data: {
    type: 'Data File',
    preview: DEFAULT_PREVIEW,
  },
};

// 확장자와 파일 타입 매핑
const EXTENSION_MAP: Record<string, keyof typeof FILE_TYPE_MAP> = {
  pdf: 'pdf',
  xls: 'excel',
  xlsx: 'excel',
  ppt: 'powerpoint',
  pptx: 'powerpoint',
  zip: 'zip',
  rar: 'zip',
  doc: 'word',
  docx: 'word',
  jpeg: 'image',
  jpg: 'image',
  png: 'image',
  svg: 'image',
  gif: 'image',
  mp4: 'video',
  json: 'data',
  xml: 'data',
};
